import React from 'react';
import { Loader2 } from 'lucide-react';
import { FormField } from '@/components/forms/FormField';
import { Input } from '@/components/ui/input';
import { FieldErrors, UseFormGetValues, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { VendorRegistrationPayload } from '@/schemas/registration.schema';
import { ShowFormError } from '@/components/forms/FormError';
import { CUISINE_LIST } from '@/constants';

const businessTypes = ['restaurant', 'cafe', 'food_truck', 'bakery', 'fast_food', 'fine_dining'];


interface Step2Props {
    register: UseFormRegister<VendorRegistrationPayload>;
    errors: FieldErrors<VendorRegistrationPayload>;
    toggleCuisine: (cuisine: string) => void;
    handlePreviousStep: () => void;
    loading: boolean;
    watch: UseFormWatch<VendorRegistrationPayload>;
    getValues: UseFormGetValues<VendorRegistrationPayload>;
}

export const Step2BusinessDetails: React.FC<Step2Props> = ({
    register,
    errors,
    toggleCuisine,
    handlePreviousStep,
    loading,
    watch,
}) => {
    const cuisineTypes = watch('cuisineTypes');
    const hasWhatsAppBusiness = watch('hasWhatsAppBusiness');

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Business Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Personal Phone" name="phone" error={errors.phone}>
                    <Input
                        {...register('phone')}
                        type="tel"
                        placeholder="+234 801 234 5678"
                        error={!!errors.phone}
                    />
                </FormField>

                <FormField label="Business Phone" name="businessPhone" error={errors.businessPhone}>
                    <Input
                        {...register('businessPhone')}
                        type="tel"
                        placeholder="+234 801 234 5679"
                        error={!!errors.businessPhone}
                    />
                </FormField>
            </div>

            <FormField label="Business Address" name="businessAddress" error={errors.businessAddress}>
                <textarea
                    {...register('businessAddress')}
                    rows={3}
                    className={`mt-1 input ${errors.businessAddress ? 'border-red-500 focus:ring-red-500' : ''
                        }`}
                    placeholder="123 Main Street, Lagos, Nigeria"
                />
            </FormField>

            <FormField label="Business Type" name="businessType" error={errors.businessType}>
                <select
                    {...register('businessType')}
                    className={`mt-1 input ${errors.businessType ? 'border-red-500 focus:ring-red-500' : ''}`}
                >
                    <option value="">Select business type</option>
                    {businessTypes.map((type) => (
                        <option key={type} value={type}>
                            {type.replace('_', ' ').toUpperCase()}
                        </option>
                    ))}
                </select>
            </FormField>

            <FormField
                label="Cuisine Types (Select all that apply)"
                name="cuisineTypes"
                error={errors.cuisineTypes?.[0]}
            >
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {CUISINE_LIST.map((cuisine) => (
                        <label key={cuisine} className="flex items-center">
                            <input
                                type="checkbox"
                                checked={cuisineTypes.includes(cuisine)}
                                onChange={() => toggleCuisine(cuisine)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">{cuisine}</span>
                        </label>
                    ))}
                </div>
                <ShowFormError message={errors.cuisineTypes?.message} />
            </FormField>

            <FormField label="Business Description (Optional)" name="description">
                <textarea
                    {...register('description')}
                    rows={2}
                    className="mt-1 input"
                    placeholder="Brief description of your restaurant..."
                />
            </FormField>

            <label className="flex items-center">
                <input
                    {...register('hasWhatsAppBusiness')}
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">I have WhatsApp Business account</span>
            </label>

            {hasWhatsAppBusiness && (
                <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                    <h4 className="text-sm font-medium text-green-800">WhatsApp Business Details</h4>

                    <FormField
                        label="WhatsApp Business ID"
                        name="whatsappBusinessId"
                        error={errors.whatsappBusinessId}
                    >
                        <Input
                            {...register('whatsappBusinessId')}
                            type="text"
                            className="mt-1"
                            placeholder="Your WhatsApp Business ID"
                        />
                    </FormField>

                    <FormField
                        label="WhatsApp Access Token"
                        name="whatsappAccessToken"
                        error={errors.whatsappAccessToken}
                    >
                        <Input
                            {...register('whatsappAccessToken')}
                            type="text"
                            className="mt-1"
                            placeholder="Your WhatsApp API access token"
                        />
                    </FormField>

                    <FormField
                        label="WhatsApp Number"
                        name="whatsAppPhone"
                        error={errors.whatsappPhoneNumber}
                    >
                        <Input
                            {...register('whatsappPhoneNumber')}
                            type="tel"
                            placeholder="+234 801 234 5679"
                            error={!!errors.businessPhone}
                        />
                    </FormField>
                </div>
            )}

            <div className="flex justify-between">
                <button type="button" onClick={handlePreviousStep} className="btn btn-secondary">
                    Previous
                </button>

                <button type="submit" className="btn btn-primary flex items-center">
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating Account...
                        </>
                    ) : (
                        'Create Account'
                    )}
                </button>
            </div>
        </div>
    );
};
